/**
 * Anasayfa "Yapılacaklar" widget'ı — 25.08 md, madde 8.
 *
 * Veri kaynağı `todo_list` (kişisel yapılacaklar). Bu tablo zaten kullanılıyordu
 * ama React tarafında yalnızca `/gorev` ekranı vardı ve listeleme ucu `done`
 * süzgeci ile LIMIT içermiyordu — canlıda bir kullanıcıda 7.374 satır var, yani
 * widget'a doğrudan bağlanamazdı. Yazma işlemleri de generic
 * `POST /form/submit-form` üzerinden yapılıyordu (sahiplik istek gövdesinden
 * geliyordu). Artık `routes/gorev.py` içindeki kendi uçları kullanılıyor.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, List, Checkbox, Input, Button, Typography, Space, Empty, Spin, message } from 'antd';
import { CheckSquareOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';

const { Text } = Typography;

const TodoWidget = ({ darkMode, limit = 8 }) => {
  const [kayitlar, setKayitlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [metin, setMetin] = useState('');
  const [ekleniyor, setEkleniyor] = useState(false);

  const getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      // Yalnızca tamamlanmamışlar; widget kısa kalmalı.
      const r = await axios.get(`${API_BASE_URL}/gorev/list`, {
        params: { done: 0, limit },
      });
      setKayitlar(r.data?.tasks || []);
    } catch (e) {
      // Anasayfa tek bir widget yüzünden bozulmasın.
      console.warn('[todo-widget] liste alınamadı:', e?.message);
    } finally {
      setYukleniyor(false);
    }
  }, [limit]);

  useEffect(() => { getir(); }, [getir]);

  const ekle = useCallback(async () => {
    const t = metin.trim();
    if (!t) return;
    setEkleniyor(true);
    try {
      await axios.post(`${API_BASE_URL}/gorev`, { metin: t });
      setMetin('');
      getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Eklenemedi');
    } finally {
      setEkleniyor(false);
    }
  }, [metin, getir]);

  const tamamla = useCallback(async (kayit) => {
    // İyimser güncelleme: kutucuk anında kaybolsun.
    setKayitlar((k) => k.filter((x) => x.id !== kayit.id));
    try {
      await axios.put(`${API_BASE_URL}/gorev/${kayit.id}`, { done: 1 });
    } catch (e) {
      message.error('Güncellenemedi');
      getir();
    }
  }, [getir]);

  const sil = useCallback(async (kayit) => {
    setKayitlar((k) => k.filter((x) => x.id !== kayit.id));
    try {
      await axios.delete(`${API_BASE_URL}/gorev/${kayit.id}`);
    } catch (e) {
      message.error('Silinemedi');
      getir();
    }
  }, [getir]);

  return (
    <Card
      title={
        <Space>
          <CheckSquareOutlined style={{ color: '#52c41a' }} />
          <Text strong>Yapılacaklar</Text>
        </Space>
      }
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: 12 }}
    >
      <Space.Compact style={{ width: '100%', marginBottom: 10 }}>
        <Input
          placeholder="Yeni madde…"
          value={metin}
          maxLength={2000}
          onChange={(e) => setMetin(e.target.value)}
          onPressEnter={ekle}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={ekleniyor} onClick={ekle} />
      </Space.Compact>

      <Spin spinning={yukleniyor}>
        {kayitlar.length === 0 && !yukleniyor ? (
          <Empty description="Bekleyen madde yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            size="small"
            dataSource={kayitlar}
            renderItem={(k) => (
              <List.Item
                actions={[
                  <Button
                    key="sil"
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => sil(k)}
                  />,
                ]}
              >
                <Checkbox onChange={() => tamamla(k)} style={{ marginRight: 8 }} />
                <span style={{ flex: 1, color: darkMode ? '#d9d9d9' : undefined }}>{k.metin}</span>
              </List.Item>
            )}
          />
        )}
      </Spin>
    </Card>
  );
};

export default TodoWidget;
